import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from '@/lib/auth/use-auth'
import { useReference } from '@/lib/reference/use-reference'
import { airportGroupIndex, countryGroupIndex } from '@/lib/reference/groups'
import { fleetColumnKey } from '@/lib/reference/fleet'
import type { Aircraft } from '@/lib/reference'
import {
  ScheduleDataProvider,
  alertsFor,
  airportIndex,
  cycleIndex,
  exceptionIndexes,
  useSchedule,
  worstSeverity,
  type AlertContext,
  type AlertSeverity,
  type FlightLeg,
  type LegAlert,
} from '@/lib/schedule'
import {
  DEFAULT_SPAN,
  UTC_CLOCK,
  UTC_RULES,
  addLocalDays,
  clampFrame,
  frameEnd,
  snapStart,
  startOfLocalDay,
  stepWindow,
  viewFor,
  type Clock,
  type SpanKey,
  type Window,
} from '@/lib/schedule/timeline'
import { formatOffset, localPartsAt, utcFromLocal, zoneStateAt } from '@/lib/time'
import { Button } from '@/components/ui/Button'
import { Callout } from '@/components/ui/Callout'
import { GanttBoard, type BoardRow } from '@/components/gantt/GanttBoard'
import {
  GanttToolbar,
  type FilterOption,
  type GanttFilters,
  type Grouping,
} from '@/components/gantt/GanttToolbar'
import { ImportDialog } from '@/components/gantt/ImportDialog'
import { LegPanel } from '@/components/gantt/LegPanel'

/** Home base. Every station in the drop is a candidate; this is the default. */
const HOME_STATION = 'SGN'

const NO_FILTERS: GanttFilters = {
  group: '',
  category: '',
  type: '',
  station: '',
  country: '',
  flight: '',
  showCancelled: true,
}

/** How often the "now" line moves. A minute is finer than a bar is wide. */
const NOW_TICK_MS = 60_000

export function GanttPage() {
  return (
    <ScheduleDataProvider>
      <GanttScreen />
    </ScheduleDataProvider>
  )
}

/**
 * Tail rotation across a period.
 *
 * Every column of it is a join into the settings screens: the rows are the
 * fleet table, the filters are its group/category/type trio, the clock comes
 * from the airport table by way of the timezone transitions, and the flagged
 * bars are the capability matrices and the four tail limits applied to legs
 * that are already flying.
 */
function GanttScreen() {
  const { can } = useAuth()
  const {
    aircraft,
    airports,
    countries,
    zoneRules,
    countryCapability,
    airportCapability,
    capabilityException,
    airportGroups,
    countryGroups,
  } = useReference()
  const { status, legs, batches, persisted, canUndo, undo } = useSchedule()

  const [span, setSpan] = useState<SpanKey>(DEFAULT_SPAN)
  const [clockMode, setClockMode] = useState<'utc' | 'local'>('local')
  const [baseStation, setBaseStation] = useState(HOME_STATION)
  /**
   * The day the operator picked. Its own state, and nothing derives it:
   * lengthening the frame, shortening it, or clamping it against the end of
   * the loaded period all leave this alone, because "which day am I looking
   * at" is the one thing on the screen the controls are not allowed to move.
   */
  const [pickedDate, setPickedDate] = useState(() => dateOf(Date.now(), UTC_CLOCK))
  // The frame's start instant, not a day: switching the axis between UTC and
  // local must relabel the board without moving it, so the start survives the
  // toggle and only the end is recomputed on the new clock.
  const [frameStart, setFrameStart] = useState(() => snapStart(Date.now(), DEFAULT_SPAN, UTC_CLOCK))
  const [grouping, setGrouping] = useState<Grouping>('aircraft')
  const [filters, setFilters] = useState<GanttFilters>(NO_FILTERS)
  const [selectedKey, setSelectedKey] = useState<string | null>(null)
  const [importing, setImporting] = useState(false)
  const [nowUtc, setNowUtc] = useState(() => Date.now())

  useEffect(() => {
    const timer = window.setInterval(() => setNowUtc(Date.now()), NOW_TICK_MS)
    return () => window.clearInterval(timer)
  }, [])

  const airportsByIata = useMemo(() => airportIndex(airports), [airports])

  const baseZone = airportsByIata.get(baseStation)?.timezone ?? ''
  const baseRules = baseZone ? zoneRules[baseZone] : undefined

  const clock = useMemo<Clock>(() => {
    if (clockMode === 'utc') return UTC_CLOCK
    // A station whose zone is missing would silently become UTC, so it keeps
    // its own name instead of borrowing one.
    return baseRules
      ? { mode: 'local', zone: baseZone, rules: baseRules }
      : { mode: 'local', zone: baseStation, rules: UTC_RULES }
  }, [clockMode, baseStation, baseZone, baseRules])

  /**
   * The period that has been loaded — what the imports declared.
   *
   * The declared period rather than the span of the legs: a file for 1 to 30
   * September covers the 30th whether or not anything flew that evening, and
   * that is the boundary the board must not walk past. Legs are the fallback
   * for a schedule that arrived without a batch record.
   */
  const period = useMemo<Window | null>(() => {
    let from = Number.POSITIVE_INFINITY
    let to = Number.NEGATIVE_INFINITY
    for (const batch of batches) {
      for (const file of batch.files) {
        from = Math.min(from, dayStart(file.scope.from, clock))
        to = Math.max(to, addLocalDays(dayStart(file.scope.to, clock), 1, clock))
      }
    }
    if (from <= to) return { startUtc: from, endUtc: to }

    for (const leg of legs) {
      from = Math.min(from, leg.stdUtc)
      to = Math.max(to, leg.staUtc)
    }
    if (from > to) return null
    return {
      startUtc: startOfLocalDay(from, clock),
      endUtc: addLocalDays(startOfLocalDay(to, clock), 1, clock),
    }
  }, [batches, legs, clock])

  /** The frame as the chosen day and frame length ask for it, before clamping. */
  const rawFrame = useMemo(
    () => ({ startUtc: frameStart, endUtc: frameEnd(frameStart, span, clock) }),
    [frameStart, span, clock],
  )

  const frame = useMemo(() => clampFrame(rawFrame, period), [rawFrame, period])

  /** Every deliberate move: the day picked, and the frame that starts there. */
  const goTo = useCallback(
    (anchorUtc: number) => {
      // Kept inside the loaded period: there is nothing to see beyond it, and
      // a date field that wanders past the data is a date field nobody trusts.
      const bounded = period
        ? Math.min(Math.max(anchorUtc, period.startUtc), period.endUtc - 60_000)
        : anchorUtc
      setPickedDate(dateOf(bounded, clock))
      setFrameStart(snapStart(bounded, span, clock))
    },
    [period, span, clock],
  )

  /** A different frame length over the same chosen day. */
  const changeSpan = useCallback(
    (next: SpanKey) => {
      setSpan(next)
      setFrameStart((current) => snapStart(current, next, clock))
    },
    [clock],
  )

  // Vietnam has no clock changes, so an empty transition list is not the test:
  // what matters is whether the station resolved to a zone at all.
  const clockReady = clock.mode === 'utc' || Boolean(baseRules)
  const periodKey = period ? `${period.startUtc}|${period.endUtc}` : ''
  const settled = useRef<string | null>(null)

  /**
   * Where the board opens: today, or the first day of the period when today
   * is not in it.
   *
   * Runs when the clock becomes real and whenever the period changes — a load,
   * an import, an undo — and never again, so it cannot argue with a move the
   * operator has made.
   */
  useEffect(() => {
    if (!clockReady || settled.current === periodKey) return
    settled.current = periodKey
    const now = Date.now()
    goTo(period && (now < period.startUtc || now >= period.endUtc) ? period.startUtc : now)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clockReady, periodKey])

  // Stations, countries and flight numbers the loaded schedule actually
  // touches. Offering the whole airport table would mean 8.672 places, all but
  // 76 of which are empty every day of the month.
  const { stationOptions, countryOptions, flightOptions, stationCountry } = useMemo(() => {
    const used = new Set<string>()
    const flights = new Set<string>()
    for (const leg of legs) {
      used.add(leg.dep)
      used.add(leg.arr)
      flights.add(leg.flightNumber)
    }
    const countryNames = new Map(countries.map((row) => [row.code, row.name] as const))
    const stationCountry = new Map<string, string>()
    const countryCodes = new Set<string>()
    for (const code of used) {
      const airport = airportsByIata.get(code)
      if (!airport?.countryCode) continue
      stationCountry.set(code, airport.countryCode)
      countryCodes.add(airport.countryCode)
    }
    return {
      // The text is what the browser type-ahead matches on, so it carries the
      // city and the airport's own name as well as the code: a station is
      // found by whichever of the three the reader happens to know.
      stationOptions: [...used].sort().map((code) => {
        const airport = airportsByIata.get(code)
        const city = airport?.municipality ?? ''
        const name = airport?.name && airport.name !== city ? airport.name : ''
        return { value: code, text: [code, city, name].filter(Boolean).join(' — ') }
      }),
      countryOptions: [...countryCodes].sort().map((code) => ({
        value: code,
        text: `${code} — ${countryNames.get(code) ?? ''}`.trim(),
      })),
      flightOptions: [...flights]
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
        .map((number) => ({ value: number, text: number })),
      stationCountry,
    }
  }, [legs, airportsByIata, countries])

  const fleetOptions = useMemo(() => {
    const narrow = (extra: (row: Aircraft) => boolean) => aircraft.filter(extra)
    const list = (rows: Aircraft[], pick: (row: Aircraft) => string): FilterOption[] =>
      [...new Set(rows.map(pick).filter(Boolean))]
        .sort()
        .map((value) => ({ value, text: value }))

    const group = resolveOption(filters.group, list(aircraft, (row) => row.group))
    const category = resolveOption(
      filters.category,
      list(aircraft, (row) => row.category),
    )
    return {
      groups: list(aircraft, (row) => row.group),
      categories: list(
        narrow((row) => !group || row.group === group),
        (row) => row.category,
      ),
      types: list(
        narrow((row) => (!group || row.group === group) && (!category || row.category === category)),
        (row) => row.type,
      ),
    }
  }, [aircraft, filters.group, filters.category])

  // Typed text resolves to a code; anything that resolves to nothing narrows
  // nothing, so a half-typed station never empties the board.
  const active = useMemo(
    () => ({
      group: resolveOption(filters.group, fleetOptions.groups),
      category: resolveOption(filters.category, fleetOptions.categories),
      type: resolveOption(filters.type, fleetOptions.types),
      station: resolveOption(filters.station, stationOptions),
      country: resolveOption(filters.country, countryOptions),
      flight: filters.flight.trim().toUpperCase(),
    }),
    [filters, fleetOptions, stationOptions, countryOptions],
  )

  const alertContext = useMemo<AlertContext>(
    () => ({
      fleetByRegn: new Map(aircraft.map((row) => [row.regn, row] as const)),
      airportsByIata,
      countryCapability,
      airportCapability,
      ...exceptionIndexes(capabilityException),
      airportGroupMembers: airportGroupIndex(airportGroups, airports, airportCapability),
      countryGroupMembers: countryGroupIndex(countryGroups, countries, airports),
      cyclesByTailDay: cycleIndex(legs),
    }),
    [
      aircraft,
      airportsByIata,
      countryCapability,
      airportCapability,
      capabilityException,
      airportGroups,
      countryGroups,
      airports,
      countries,
      legs,
    ],
  )

  const alertsByLeg = useMemo(() => {
    const out = new Map<string, LegAlert[]>()
    for (const leg of legs) {
      const alerts = alertsFor(leg, alertContext)
      if (alerts.length > 0) out.set(leg.key, alerts)
    }
    return out
  }, [legs, alertContext])

  const severityByLeg = useMemo(() => {
    const out = new Map<string, AlertSeverity>()
    for (const [key, alerts] of alertsByLeg) {
      const severity = worstSeverity(alerts)
      if (severity) out.set(key, severity)
    }
    return out
  }, [alertsByLeg])

  const severityOf = useCallback(
    (leg: FlightLeg) => severityByLeg.get(leg.key) ?? null,
    [severityByLeg],
  )

  // Leg-level filters, applied once; the frame is applied per row afterwards.
  const visibleLegs = useMemo(
    () =>
      legs.filter((leg) => {
        if (!filters.showCancelled && leg.cancelledAt) return false
        if (active.station && leg.dep !== active.station && leg.arr !== active.station) return false
        if (
          active.country &&
          stationCountry.get(leg.dep) !== active.country &&
          stationCountry.get(leg.arr) !== active.country
        ) {
          return false
        }
        if (active.flight && !leg.flightNumber.toUpperCase().includes(active.flight)) return false
        return true
      }),
    [legs, filters.showCancelled, active, stationCountry],
  )

  const legsByTail = useMemo(() => {
    const out = new Map<string, FlightLeg[]>()
    for (const leg of visibleLegs) {
      const list = out.get(leg.regn)
      if (list) list.push(leg)
      else out.set(leg.regn, [leg])
    }
    for (const list of out.values()) list.sort((a, b) => a.stdUtc - b.stdUtc)
    return out
  }, [visibleLegs])

  const fleet = useMemo(
    () =>
      aircraft
        .filter(
          (tail) =>
            (!active.group || tail.group === active.group) &&
            (!active.category || tail.category === active.category) &&
            (!active.type || tail.type === active.type),
        )
        .sort((a, b) => a.regn.localeCompare(b.regn)),
    [aircraft, active.group, active.category, active.type],
  )

  const view = useMemo(() => viewFor(frame, span, clock), [frame, span, clock])

  /**
   * What the board may be scrolled across: **the loaded period, plus a day at
   * each end**.
   *
   * Not a multiple of the frame — an import declares 1 to 30 September, so
   * that is what the board offers, rather than two months nobody uploaded.
   * The extra day on each side is there because a leg that leaves late on the
   * 30th lands on the 1st: the period bounds where the frame may go, the
   * canvas is what has to be drawn for those legs to be drawn whole. The
   * current view is folded in as well, so a board with nothing loaded still
   * has something to draw.
   */
  const canvas = useMemo<Window>(() => {
    if (!period) return view
    return {
      startUtc: Math.min(addLocalDays(period.startUtc, -1, clock), view.startUtc),
      endUtc: Math.max(addLocalDays(period.endUtc, 1, clock), view.endUtc),
    }
  }, [period, view, clock])

  const narrowed = Boolean(active.station || active.country || active.flight)
  const rows = useMemo(
    () => buildRows(fleet, legsByTail, canvas, grouping, narrowed),
    [fleet, legsByTail, canvas, grouping, narrowed],
  )

  const inFrame = useMemo(
    () =>
      rows.reduce(
        (total, row) =>
          total +
          row.legs.filter((leg) => leg.staUtc > frame.startUtc && leg.stdUtc < frame.endUtc).length,
        0,
      ),
    [rows, frame],
  )
  const flaggedInFrame = useMemo(
    () =>
      rows.reduce(
        (total, row) =>
          total +
          row.legs.filter(
            (leg) =>
              leg.staUtc > frame.startUtc &&
              leg.stdUtc < frame.endUtc &&
              severityByLeg.has(leg.key),
          ).length,
        0,
      ),
    [rows, frame, severityByLeg],
  )

  const selected = selectedKey ? (legs.find((leg) => leg.key === selectedKey) ?? null) : null
  const offsetAtStart = zoneStateAt(clock.zone, clock.rules, frame.startUtc).offsetSeconds
  // The date field offers exactly the days that were loaded.
  const dateRange = period
    ? { min: dateOf(period.startUtc, clock), max: dateOf(period.endUtc - 60_000, clock) }
    : { min: '', max: '' }

  const localZones = useMemo(() => {
    const codes = new Set<string>([HOME_STATION, baseStation, ...stationOptions.map((s) => s.value)])
    return [...codes]
      .filter((code) => airportsByIata.has(code))
      .sort()
      .map((code) => {
        const zone = airportsByIata.get(code)?.timezone ?? ''
        const rules = zoneRules[zone]
        return {
          code,
          offsetSeconds: rules ? zoneStateAt(zone, rules, frame.startUtc).offsetSeconds : 0,
        }
      })
  }, [stationOptions, baseStation, airportsByIata, zoneRules, frame.startUtc])

  const lastBatch = batches[batches.length - 1]

  return (
    <section>
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2">
        <h1 className="t-title-lg text-ink">Gantt</h1>
        <p className="t-caption text-muted">
          <span className="t-number-sm text-ink">{rows.filter((row) => row.kind === 'tail').length}</span>{' '}
          tails · <span className="t-number-sm text-ink">{inFrame}</span> legs
          {flaggedInFrame > 0 && (
            <>
              {' · '}
              <span className="t-number-sm text-semantic-down">{flaggedInFrame}</span> flagged
            </>
          )}
          {clockMode === 'local' && ` · ${formatOffset(offsetAtStart)}`}
          {!persisted && ' · session only'}
        </p>
      </div>

      <div className="mt-6">
        <GanttToolbar
          span={span}
          onSpan={changeSpan}
          clock={clock}
          onClockMode={setClockMode}
          baseStation={baseStation}
          onBaseStation={setBaseStation}
          localZones={localZones}
          dateValue={pickedDate}
          dateMin={dateRange.min}
          dateMax={dateRange.max}
          onDate={(value) => {
            const [year, month, day] = value.split('-').map(Number)
            if (!year || !month || !day) return
            goTo(utcFromLocal(clock.zone, clock.rules, { year, month, day, hour: 0, minute: 0 }))
          }}
          onStep={(direction) => goTo(stepWindow(rawFrame, span, clock, direction))}
          onNow={() => goTo(Date.now())}
          grouping={grouping}
          onGrouping={setGrouping}
          filters={filters}
          onFilters={setFilters}
          options={{
            groups: fleetOptions.groups,
            categories: fleetOptions.categories,
            types: fleetOptions.types,
            stations: stationOptions,
            countries: countryOptions,
            flights: flightOptions,
          }}
        >
          {canUndo && (
            <Button variant="tertiary" className="!t-caption-strong !h-9 !px-2" onClick={() => undo()}>
              Undo import
            </Button>
          )}
          {can('schedule.edit') && (
            <Button
              variant="secondary"
              className="!t-caption-strong !h-9 !px-4"
              onClick={() => setImporting(true)}
            >
              Import
            </Button>
          )}
        </GanttToolbar>
      </div>

      {status === 'error' && (
        <Callout tone="error" className="mt-6" title="Schedule unavailable">
          The stored schedule could not be read.
        </Callout>
      )}

      {/* The panel is laid over the board rather than beside it: a board that
          changes width when a leg is opened has to be re-read from scratch,
          and the point of opening a leg is not to lose your place. */}
      <div className="relative mt-4">
        {legs.length === 0 && status === 'ready' ? (
          <EmptyBoard canImport={can('schedule.edit')} onImport={() => setImporting(true)} />
        ) : (
          <GanttBoard
            rows={rows}
            view={view}
            canvas={canvas}
            span={span}
            clock={clock}
            nowUtc={nowUtc}
            clockLabel={clockMode === 'utc' ? 'UTC' : baseStation}
            selectedKey={selectedKey}
            severityOf={severityOf}
            onSelect={(leg) => setSelectedKey((current) => (current === leg.key ? null : leg.key))}
          />
        )}

        {selected && (
          <div
            className="absolute inset-y-0 right-0 z-40 w-[340px] max-w-full overflow-y-auto
                       rounded-r-[24px] border-l border-hairline bg-canvas shadow-soft"
          >
            <LegPanel
              leg={selected}
              clock={clock}
              clockLabel={clockMode === 'utc' ? 'UTC' : baseStation}
              tail={aircraft.find((row) => row.regn === selected.regn) ?? null}
              depAirport={airportsByIata.get(selected.dep) ?? null}
              arrAirport={airportsByIata.get(selected.arr) ?? null}
              zoneRules={zoneRules}
              alerts={alertsByLeg.get(selected.key) ?? []}
              onClose={() => setSelectedKey(null)}
            />
          </div>
        )}
      </div>

      {lastBatch && (
        <p className="t-caption mt-3 text-muted">
          Last import {lastBatch.at.slice(0, 16).replace('T', ' ')} ·{' '}
          {lastBatch.files.map((file) => `${file.scope.from} → ${file.scope.to}`).join(' · ')} ·{' '}
          <span className="t-number-sm text-ink">+{lastBatch.plan.added}</span>{' '}
          <span className="t-number-sm text-ink">~{lastBatch.plan.changed}</span>{' '}
          <span className="t-number-sm text-semantic-down">−{lastBatch.plan.removed}</span>
        </p>
      )}

      {importing && <ImportDialog onClose={() => setImporting(false)} />}
    </section>
  )
}

/**
 * Typed text -> the value it names.
 *
 * An exact match on either the code or the whole suggestion wins; failing
 * that, text that still has exactly one candidate left resolves to it, so
 * "Inch" is as good as picking "ICN — Incheon" from the list. Anything else
 * resolves to nothing and narrows nothing.
 */
function resolveOption(text: string, options: FilterOption[]): string {
  const raw = text.trim().toLowerCase()
  if (!raw) return ''
  const exact = options.find(
    (option) => option.value.toLowerCase() === raw || option.text.toLowerCase() === raw,
  )
  if (exact) return exact.value
  const matches = options.filter((option) => option.text.toLowerCase().includes(raw))
  return matches.length === 1 ? matches[0].value : ''
}

function EmptyBoard({ canImport, onImport }: { canImport: boolean; onImport: () => void }) {
  return (
    <div className="grid min-h-[320px] place-items-center rounded-[24px] border border-hairline">
      <div className="text-center">
        <p className="t-body-md text-muted">No schedule loaded</p>
        {canImport && (
          <Button className="mt-6" onClick={onImport}>
            Import
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Rows, in the order the board draws them.
 *
 * Grouped by the group/category/type trio by default, because that is the
 * dimension every permission matrix is keyed by and the first question of a
 * swap is "what else is this kind of aircraft".
 *
 * Unassigned legs come first and only when there are any: a flight with no
 * registration is the one thing on the board that nobody has dealt with yet,
 * and it has no tail row to sit on.
 */
function buildRows(
  fleet: Aircraft[],
  legsByTail: Map<string, FlightLeg[]>,
  canvas: { startUtc: number; endUtc: number },
  grouping: Grouping,
  narrowed: boolean,
): BoardRow[] {
  const onCanvas = (regn: string) =>
    (legsByTail.get(regn) ?? []).filter(
      (leg) => leg.staUtc > canvas.startUtc && leg.stdUtc < canvas.endUtc,
    )

  const rows: BoardRow[] = []
  const unassigned = onCanvas('')
  if (unassigned.length > 0) {
    rows.push({ kind: 'tail', key: '__unassigned', label: 'NO REG', sub: '', legs: unassigned })
  }

  // A filter that left every tail on screen would look like it had not been
  // applied; without one, the fleet is the row set whether it flew or not.
  if (grouping === 'regn') {
    for (const tail of fleet) {
      const legs = onCanvas(tail.regn)
      if (narrowed && legs.length === 0) continue
      rows.push({ kind: 'tail', key: tail.regn, label: tail.regn, sub: tail.type, legs })
    }
    return rows
  }

  const byTrio = new Map<string, Aircraft[]>()
  for (const tail of fleet) {
    const key = fleetColumnKey(tail)
    const list = byTrio.get(key)
    if (list) list.push(tail)
    else byTrio.set(key, [tail])
  }

  for (const key of [...byTrio.keys()].sort()) {
    const tails = byTrio.get(key) ?? []
    const section: BoardRow[] = []
    for (const tail of tails) {
      const legs = onCanvas(tail.regn)
      if (narrowed && legs.length === 0) continue
      section.push({ kind: 'tail', key: tail.regn, label: tail.regn, sub: '', legs })
    }
    if (section.length === 0) continue
    rows.push({
      kind: 'group',
      key: `group-${key}`,
      label: key.replace(/\//g, ' · '),
      sub: `${section.length} tails`,
      legs: [],
    })
    rows.push(...section)
  }
  return rows
}

function pad(value: number): string {
  return value < 10 ? `0${value}` : String(value)
}

/** An instant as the calendar date it falls on, read on the board's clock. */
function dateOf(utcMs: number, clock: Clock): string {
  const parts = localPartsAt(utcMs, zoneStateAt(clock.zone, clock.rules, utcMs).offsetSeconds)
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}`
}

/** "2026-09-01" -> midnight local to the clock the board is read in. */
function dayStart(date: string, clock: Clock): number {
  const [year, month, day] = date.split('-').map(Number)
  if (!year || !month || !day) return Number.POSITIVE_INFINITY
  return utcFromLocal(clock.zone, clock.rules, { year, month, day, hour: 0, minute: 0 })
}
